const CLARITY_PROJECT_ID = process.env.REACT_APP_CLARITY_PROJECT_ID;
const CLARITY_SCRIPT_ID = "clarity-script";

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

const isProduction = () =>
    isBrowser() &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1";

const isInternalTraffic = () =>
    isBrowser() && localStorage.getItem("ga_internal") === "1";

const shouldLoadClarity = () =>
    Boolean(CLARITY_PROJECT_ID) && isProduction() && !isInternalTraffic();

const getClarity = () =>
    isBrowser() && typeof window.clarity === "function" ? window.clarity : null;

const sanitizeValue = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized ? normalized.slice(0, 255) : null;
};

const ensureClarityStub = () => {
    if (!isBrowser() || typeof window.clarity === "function") return;

    window.clarity = function clarity() {
        window.clarity.q = window.clarity.q || [];
        window.clarity.q.push(arguments);
    };
};

const callClarity = (...args) => {
    const clarity = getClarity();
    if (!clarity) return;
    clarity(...args);
};

export const initClarity = () => {
    if (!shouldLoadClarity()) return;
    if (document.getElementById(CLARITY_SCRIPT_ID)) return;

    ensureClarityStub();

    const script = document.createElement("script");
    script.id = CLARITY_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
    document.head.appendChild(script);
};

export const identifyClarityUser = ({ userId, pageId } = {}) => {
    const customId = sanitizeValue(userId);
    if (!customId) return;

    const customPageId = sanitizeValue(pageId);
    callClarity("identify", customId, undefined, customPageId);
};

export const setClarityTag = (key, value) => {
    const tagKey = sanitizeValue(key);
    if (!tagKey) return;

    if (Array.isArray(value)) {
        const values = value.map(sanitizeValue).filter(Boolean);
        if (!values.length) return;
        callClarity("set", tagKey, values.length === 1 ? values[0] : values);
        return;
    }

    const tagValue = sanitizeValue(value);
    if (!tagValue) return;
    callClarity("set", tagKey, tagValue);
};

export const setClarityTags = (tags = {}) => {
    Object.entries(tags).forEach(([key, value]) => setClarityTag(key, value));
};

export const trackClarityEvent = (name) => {
    const eventName = sanitizeValue(name);
    if (!eventName) return;
    callClarity("event", eventName);
};

const YAHOO_LEAGUE_ROUTE_RE =
    /^\/fantasy_stats\/(?:league|simulation|league-history|league-cup|trade-hub|uh-oh-too-early\/[^/]+)\/yahoo\//;

export const getClarityRouteMetadata = (pathname = "") => {
    // Yahoo's API terms do not allow Yahoo league data to be captured by a
    // third-party session-replay service, so Clarity stays off on those routes.
    if (YAHOO_LEAGUE_ROUTE_RE.test(pathname)) {
        return {
            appArea: "league",
            routeName: "league_yahoo",
            featureSurface: "league",
            clarityEnabled: false,
        };
    }

    if (pathname.startsWith("/playoff-pool")) {
        return {
            appArea: "playoff_pool",
            routeName: "playoff_pool",
            featureSurface: "playoff_pool",
            clarityEnabled: false,
        };
    }

    if (pathname === "/") {
        return {
            appArea: "public",
            routeName: "landing",
            featureSurface: "landing",
            clarityEnabled: true,
        };
    }

    if (pathname === "/dashboard") {
        return {
            appArea: "main_app",
            routeName: "dashboard",
            featureSurface: "dashboard",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/demo")) {
        return {
            appArea: "public",
            routeName: "demo",
            featureSurface: "demo",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/login")) {
        return {
            appArea: "auth",
            routeName: "login",
            featureSurface: "login",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/register")) {
        return {
            appArea: "auth",
            routeName: "register",
            featureSurface: "register",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/password-reset/confirm")) {
        return {
            appArea: "auth",
            routeName: "password_reset_confirm",
            featureSurface: "password_reset",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/password-reset")) {
        return {
            appArea: "auth",
            routeName: "password_reset",
            featureSurface: "password_reset",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/share/")) {
        return {
            appArea: "shared_league",
            routeName: "shared_league",
            featureSurface: "shared_league",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/fantasy_stats/league-history/")) {
        return {
            appArea: "main_app",
            routeName: "league_history",
            featureSurface: "league_history",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/fantasy_stats/simulation/")) {
        return {
            appArea: "main_app",
            routeName: "league_simulation",
            featureSurface: "league_simulation",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/fantasy_stats/league/") && pathname.endsWith("/settings/")) {
        return {
            appArea: "main_app",
            routeName: "league_settings",
            featureSurface: "league_settings",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/fantasy_stats/league/")) {
        return {
            appArea: "main_app",
            routeName: "league",
            featureSurface: "league",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/fantasy_stats/uh-oh-too-early/")) {
        return {
            appArea: "main_app",
            routeName: "league_too_early",
            featureSurface: "league",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/fantasy_stats/access-denied/")) {
        return {
            appArea: "main_app",
            routeName: "league_access_denied",
            featureSurface: "league",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/fantasy_stats/invalid-league/")) {
        return {
            appArea: "main_app",
            routeName: "invalid_league",
            featureSurface: "league",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/contact")) {
        return {
            appArea: "public",
            routeName: "contact",
            featureSurface: "contact",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/methodology")) {
        return {
            appArea: "public",
            routeName: "methodology",
            featureSurface: "methodology",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/admin/ktc-mappings")) {
        return {
            appArea: "admin",
            routeName: "admin_ktc_mappings",
            featureSurface: "admin",
            clarityEnabled: true,
        };
    }

    if (pathname.startsWith("/admin/historical-store")) {
        return {
            appArea: "admin",
            routeName: "admin_historical_store",
            featureSurface: "admin",
            clarityEnabled: true,
        };
    }

    return {
        appArea: "public",
        routeName: "unknown",
        featureSurface: "unknown",
        clarityEnabled: true,
    };
};

export const buildClarityPageId = (pathname = "") =>
    getClarityRouteMetadata(pathname).routeName;
